import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCompanies } from "../../../api/company";
import { fetchAccounts } from "../../../api/user";
import {
  EnumDeviceType,
  ENUMDEVICETYPE_OPTION,
} from "../../../generated/enums";
import { Account, Company } from "../../../generated/types";
import { Button } from "../../common/Button";
import { FormInput, FormSelect } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";
import { PasswordField } from "../../common/PasswordField";

type LinkDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type LinkDeviceFormData = {
  company: number;
  user: number;
  type: EnumDeviceType;
  adresse_mac: string;
  password: string;
};

const ConfirmationModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirmation"
      icon="check-circle"
      maxWidth="sm"
    >
      <div className="text-center py-4">
        <div className="mb-4 text-primary">
          <FontAwesomeIcon icon="check-circle" size="3x" />
        </div>
        <p className="text-lg mb-6">Le produit a bien été créé.</p>
        <Button onClick={onClose} className="w-full">
          Fermer
        </Button>
      </div>
    </Modal>
  );
};

export const LinkDeviceModal = ({ isOpen, onClose }: LinkDeviceModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LinkDeviceFormData>({
    company: -1,
    user: -1,
    type: "avitouch",
    adresse_mac: "",
    password: "",
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const COMPANIES_OPTION: {
    value: number;
    label: string;
  }[] = companies.map((company) => ({
    value: company.id,
    label: `${company.company_name} (${company.siret})`,
  }));
  const USERS_OPTION: {
    value: number;
    label: string;
  }[] = users.map((user) => ({
    value: user.id,
    label: `${user.last_name} ${user.first_name} (${user.type})`,
  }));

  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    const fetchCompanys = async () => {
      if (isOpen) {
        setIsLoading(true);

        const companiesResult = await fetchCompanies();

        if (companiesResult.error) {
          console.error(
            t("error-company-list-fetch-failed"),
            companiesResult.error
          );
          setError(t("error-company-list-fetch-failed"));

          setIsLoading(false);
          return;
        } else {
          setCompanies(companiesResult.Companys);

          setIsLoading(false);
        }
      }
    };

    const fetchUsers = async () => {
      if (isOpen) {
        setIsLoading(true);

        const usersResult = await fetchAccounts(); // TO DO : mettre la requête des comptes par entreprise

        if (usersResult.error) {
          console.error(t("error-user-list-fetch-failed"), usersResult.error);
          setError(t("error-user-list-fetch-failed"));

          setIsLoading(false);
          return;
        } else {
          setUsers(usersResult.Users);

          setIsLoading(false);
        }
      }
    };

    fetchCompanys();
    fetchUsers();
  }, [isOpen, t]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value =
      e.target.type === "number" ? Number(e.target.value) : e.target.value;

    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TO DO : requête pour associer le produit

    console.log("Link product:", {
      product: formData,
    });

    setShowConfirmation(true);
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
    setFormData({
      company: -1,
      user: -1,
      type: "avitouch",
      adresse_mac: "",
      password: "",
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("device-link-title")}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <FormSelect
            label="Entreprise existante"
            name="company"
            value={formData.company}
            onChange={handleChange}
            options={COMPANIES_OPTION}
            disabled={isLoading}
            //error={errors.company}
            required
          />

          <FormSelect
            label="Compte"
            name="user"
            value={formData.user}
            onChange={handleChange}
            options={USERS_OPTION}
            disabled={isLoading}
            //error={errors.company}
            required
          />

          <FormSelect
            label={t("device-type")}
            name="type"
            value={formData.type}
            onChange={handleChange}
            options={ENUMDEVICETYPE_OPTION.filter(
              (option) => option.value !== "serenity"
            )}
            required
          />

          <FormInput
            label={t("device-adresse-mac")}
            name="adresse_mac"
            value={formData.adresse_mac}
            onChange={handleChange}
            required
          />
          <PasswordField
            label={t("device-link-password")}
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />

          <ModalActions>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("button-cancel")}
            </Button>
            <Button type="submit">{t("device-link-submit")}</Button>
          </ModalActions>
        </form>
      </Modal>
      <ConfirmationModal isOpen={showConfirmation} onClose={handleClose} />
    </>
  );
};
